/**
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../logger';

const logger = Logger.getInstance();

enum RuleOperator {
  EQ = 'eq',
  NOEQ = 'noeq',
  IN = 'in',
  ALLIN = 'allin',
  ANYIN = 'anyin',
  NOANYIN = 'noanyin',
  NOIN = 'noin',
  ANYPATH = 'anypath',
  NOANYPATH = 'noanypath',
  ANYEQ = 'anyeq',
  NOANYEQ = 'noanyeq',
  LTE = 'lte',
  GTE = 'gte',
}

enum CompoundOperator {
  AND = 'AND',
  OR = 'OR',
}

const ADSERVER_KV_FACT_PREFIX = 'page.kvs.';
// Set by the fact store once page-level ad server targeting has been harvested
const PAGE_KVS_HARVESTED_FACT = 'page.kvs.$harvested';
const PATH_SEPARATOR_CHAR_CODE = 47; // '/'

const KNOWN_OPERATORS: ReadonlySet<string> = new Set(Object.values(RuleOperator));
// Rules are re-parsed on every eligibility evaluation, so an unknown operator
// is reported once per operator per page rather than per rule instance
const reportedUnknownOperators = new Set<string>();

class Rule {
  fact: string;
  operator: RuleOperator;
  value: any;
  // Lower-cased string entries of `value`, precomputed for the path operators
  // only. Ad servers match ad unit codes case-insensitively — a page can
  // request 'tech_techradar' against a registered code of 'Tech_TechRadar'
  // and still serve — so path rules imported from line targeting must not
  // depend on the page's casing. Lowering happens here rather than in
  // evaluatePathCondition to keep per-value allocations off the hot
  // candidate-selection path. Non-string and empty entries are dropped: an
  // empty string would pass the boundary check for every '/'-prefixed path,
  // turning a malformed stored rule into a wildcard.
  private pathValues: string[] | null;

  constructor(factKey: string, operator: RuleOperator, value: any) {
    if (typeof factKey !== 'string') {
      throw new Error('Fact key is required to be a string');
    }
    // Ad servers evaluate KV targeting keys case-insensitively, so harvested
    // page.kvs facts are stored lower cased; rules imported from line
    // targeting can carry mixed-case keys and must be normalised to match
    const loweredFactKey = factKey.toLowerCase();
    this.fact = loweredFactKey.startsWith(ADSERVER_KV_FACT_PREFIX) ? loweredFactKey : factKey;
    this.operator = operator;
    this.value = value;
    if (
      (operator === RuleOperator.ANYPATH || operator === RuleOperator.NOANYPATH) &&
      Array.isArray(value)
    ) {
      this.pathValues = [];
      for (let i = 0; i < value.length; i++) {
        const entry = value[i];
        if (typeof entry === 'string' && entry.length > 0) {
          this.pathValues.push(entry.toLowerCase());
        }
      }
    } else {
      this.pathValues = null;
    }
    if (!KNOWN_OPERATORS.has(operator) && !reportedUnknownOperators.has(operator)) {
      reportedUnknownOperators.add(operator);
      logger.error(`Ignoring rule(s) with unsupported operator: ${operator}`);
    }
  }

  /**
   * A rule is evaluable only when its fact is present. Conditions on absent
   * facts are ignored (fail open) rather than failing eligibility: rules are
   * imported from ad server line targeting, and a fact can be legitimately
   * unavailable in a given environment (older tag version, app traffic with
   * no GPT slot, a KV the page does not expose). The ad server still enforces
   * its own targeting, so skipping costs at most a wasted candidate, whereas
   * failing closed silently blocks delivery.
   *
   * A rule whose operator this engine version does not recognise is skipped
   * for the same reason: operators are added over time, and stored rules can
   * reach an engine that predates them (an older pinned or cached build).
   * Throwing here would abort candidate selection on the publisher page.
   */
  public isEvaluable(facts: { [key: string]: any }): boolean {
    return facts[this.fact] !== undefined && KNOWN_OPERATORS.has(this.operator);
  }

  public evaluate(factValue: any): boolean {
    switch (this.operator) {
      case RuleOperator.EQ:
        return factValue === this.value;
      case RuleOperator.NOEQ:
        return factValue !== this.value;
      case RuleOperator.IN:
        if (typeof factValue == 'string') {
          return factValue.includes(this.value);
        }
        return (
          Array.isArray(factValue) &&
          this.evaluateArrayCondition(this.value, factValue, Array.isArray(this.value))
        );

      case RuleOperator.ALLIN:
        return Array.isArray(factValue) && this.evaluateArrayCondition(this.value, factValue, true);

      case RuleOperator.ANYIN:
        if (Array.isArray(factValue) && Array.isArray(this.value)) {
          return this.evaluateArrayCondition(this.value, factValue, false);
        } else if (typeof factValue === 'string' && Array.isArray(this.value)) {
          return this.value.some((element) => factValue.includes(element));
        }
        return false;

      case RuleOperator.NOANYIN:
        if (Array.isArray(factValue) && Array.isArray(this.value)) {
          return !this.evaluateArrayCondition(this.value, factValue, false);
        } else if (typeof factValue === 'string' && Array.isArray(this.value)) {
          return !this.value.some((element) => factValue.includes(element));
        }
        return true;

      case RuleOperator.NOIN:
        if (typeof factValue == 'string') {
          return !factValue.includes(this.value);
        }
        return (
          Array.isArray(factValue) &&
          !this.evaluateArrayCondition(this.value, factValue, Array.isArray(this.value))
        );
      case RuleOperator.ANYPATH:
        if (Array.isArray(factValue) && Array.isArray(this.value)) {
          return this.evaluateArrayCondition(this.value, factValue, false);
        } else if (typeof factValue === 'string' && this.pathValues !== null) {
          return this.evaluatePathCondition(this.pathValues, factValue);
        }
        return false;

      case RuleOperator.NOANYPATH:
        if (Array.isArray(factValue) && Array.isArray(this.value)) {
          return !this.evaluateArrayCondition(this.value, factValue, false);
        } else if (typeof factValue === 'string' && this.pathValues !== null) {
          return !this.evaluatePathCondition(this.pathValues, factValue);
        }
        return true;

      case RuleOperator.ANYEQ:
        if (Array.isArray(factValue) && Array.isArray(this.value)) {
          return this.evaluateArrayCondition(this.value, factValue, false);
        } else if (typeof factValue === 'string' && Array.isArray(this.value)) {
          return this.value.includes(factValue);
        }
        return false;

      case RuleOperator.NOANYEQ:
        if (Array.isArray(factValue) && Array.isArray(this.value)) {
          return !this.evaluateArrayCondition(this.value, factValue, false);
        } else if (typeof factValue === 'string' && Array.isArray(this.value)) {
          return !this.value.includes(factValue);
        }
        return true;

      case RuleOperator.LTE:
        return this.evaluateComparison(this.value, factValue, (a, b) => a <= b);
      case RuleOperator.GTE:
        return this.evaluateComparison(this.value, factValue, (a, b) => a >= b);
      default:
        // Unreachable via Engine.run and CompoundRule.evaluate, which skip
        // rules whose operator is unknown (see isEvaluable). Kept fail-open
        // for direct callers: an unrecognised operator must never throw
        // during candidate selection on a publisher page.
        logger.error(`Ignoring rule(s) with unsupported operator: ${this.operator}`);
        return false;
    }
  }

  /**
   * True when the fact equals any of the values, or sits beneath one in the
   * path hierarchy, i.e. factValue === value || factValue.startsWith(value + '/').
   * Unlike substring matching, a value can never bleed across a path segment
   * boundary: '/net/site' matches '/net/site/slot' but not '/net/sitecom/slot'.
   *
   * Comparison is case-insensitive, matching how ad servers resolve ad unit
   * codes: `values` must be the constructor-lowered `pathValues` (non-string
   * and empty entries already dropped), and the fact is lowered once here.
   * The separator check uses charCodeAt so no further strings are allocated —
   * this runs on the hot candidate-selection path.
   */
  private evaluatePathCondition(values: string[], factValue: string): boolean {
    const loweredFactValue = factValue.toLowerCase();
    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      if (
        loweredFactValue === value ||
        (loweredFactValue.length > value.length &&
          loweredFactValue.charCodeAt(value.length) === PATH_SEPARATOR_CHAR_CODE &&
          loweredFactValue.startsWith(value))
      ) {
        return true;
      }
    }
    return false;
  }

  private evaluateArrayCondition(value: any, factValue: any[], matchAll: boolean = false): boolean {
    if (Array.isArray(value) && matchAll) {
      return value.every((element) => factValue.includes(element));
    } else if (Array.isArray(value) && !matchAll) {
      return value.some((element) => factValue.includes(element));
    }
    return factValue.includes(value);
  }

  /**
   * Handles expressions like '3/d', '10/w', etc.
   * The factValue should be an array of timestamps.
   */
  private evaluateComparison(
    value: string | number,
    factValue: any,
    comparator: (a: number, b: number) => boolean,
  ): boolean {
    if (typeof value === 'number') {
      return comparator(value, factValue);
    }

    // Frequncy capping expressions like '3/d', '10/w', etc.
    const match = /^(\d+)\s*\/\s*([hdw])$/i.exec(value);
    if (!match || !Array.isArray(factValue)) return false;

    const limit = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();

    const now = Date.now();
    const windowStart = {
      h: now - 1 * 60 * 60 * 1000,
      d: now - 1 * 24 * 60 * 60 * 1000,
      w: now - 7 * 24 * 60 * 60 * 1000,
    }[unit];

    const recentViews = factValue.filter((ts: number) => ts >= (windowStart as number));

    return comparator(recentViews.length, limit);
  }
}

class CompoundRule {
  op: CompoundOperator;
  rules: (Rule | CompoundRule)[];

  constructor(operator: CompoundOperator, rules: (Rule | CompoundRule)[]) {
    if (rules.length < 2) {
      throw new Error('Compound rule must have at least 2 rules');
    }
    this.op = operator;
    this.rules = rules;
  }

  /**
   * A compound rule is evaluable when at least one child is. Callers must
   * check this before calling evaluate: a compound with no evaluable children
   * carries no signal and must be skipped, not evaluated.
   */
  public isEvaluable(facts: { [key: string]: any }): boolean {
    for (const rule of this.rules) {
      if (rule.isEvaluable(facts)) {
        return true;
      }
    }
    return false;
  }

  public evaluate(facts: { [key: string]: any }): boolean {
    // Children on absent facts are skipped (fail open): AND requires every
    // evaluable child to pass; OR requires some evaluable child to pass.
    switch (this.op) {
      case CompoundOperator.AND:
        return this.rules.every(
          (rule) => !rule.isEvaluable(facts) || this.evaluateRule(rule, facts),
        );
      case CompoundOperator.OR:
        return this.rules.some(
          (rule) => rule.isEvaluable(facts) && this.evaluateRule(rule, facts),
        );
      default:
        throw new Error(`Unsupported compound operator: ${this.op}`);
    }
  }

  private evaluateRule(rule: Rule | CompoundRule, facts: { [key: string]: any }): boolean {
    if (rule instanceof Rule) {
      return rule.evaluate(facts[rule.fact]);
    } else if (rule instanceof CompoundRule) {
      return rule.evaluate(facts);
    }
    throw new Error('Invalid rule type');
  }
}

type RuleNode = Rule | CompoundRule;

export {
  ADSERVER_KV_FACT_PREFIX,
  CompoundOperator,
  CompoundRule,
  PAGE_KVS_HARVESTED_FACT,
  Rule,
  RuleOperator,
};
export type { RuleNode };
